import { Tag } from '@components';
import { Box, Grid, Typography } from '@mui/material';
import { Formik } from 'formik';
import { Eye } from 'iconsax-react';
import { type MutableRefObject, type ReactElement, useContext, useRef, useState } from 'react';

import { SlabContext } from '@core/context/SlabContext';
import {
  NewQuoteSchemaFormik,
  type QuoteFormikType,
  type QuoteSubmitType,
  type RequiredQuoteFieldsInfo,
} from '@core/formik/quote';
import Enums from '@core/types/api/Enums';
import type { Product } from '@core/types/api/Product/Product';
import type { QuoteArchiveResult } from '@core/types/api/QuoteArchiveResult/QuoteArchiveResult';
import { QuoteStatus } from '@core/types/api/QuoteStatus/QuoteStatus';
import { hasStringValue, hasValue } from '@core/utils/equality';
import { FormErrorNotification } from '@core/utils/formikHelpers';
import { canEditCurrentRevision } from '@core/utils/quoteDrawerUtilities';
import { applyTierPricingToProductsByPlants } from '@core/utils/quoteHelpers';
import { NIL_UUID, isValidUUID } from '@core/utils/uuid';
import { ArchivedPlantWarning } from '@web/components/ArchivedPlantWarning/ArchivedPlantWarning';
import { InfoBanner } from '@web/components/Banner/InfoBanner';
import { DrawerFormFooter } from '@web/components/DrawerFormFooter/DrawerFormFooter';
import type { FileUploadsManager } from '@web/components/Files';
import { LoadingSpinner } from '@web/components/LoadingSpinner/LoadingSpinner';
import { SlabButton } from '@web/components/SlabButton/SlabButton';
import { DrawerCloseHandler } from '@web/components/SlabDrawer/DrawerCloseHandler';
import { SlabDrawerHeader } from '@web/components/SlabDrawer/SlabDrawerHeader';
import { SlabDrawerLayout } from '@web/components/SlabDrawer/SlabDrawerLayout';
import { useDrawerCloseGuard } from '@web/components/SlabDrawer/useDrawerCloseGuard';
import { useArchiveManager } from '@web/hooks/useArchiveManager/useArchiveManager';
import { useSlabQuery } from '@web/hooks/useSlabQuery';
import { useTenantMargins } from '@web/hooks/useTenantMargins';
import { getTenantRequiresApprovals } from '@web/utils/TenantConfig';

import { QuoteDrawerMode, useQuoteDrawerContext } from '../QuoteDrawer';
import {
  type QuickProjectMode,
  type QuoteDrawerFormikType,
  emptyQuickQuoteAddress,
} from '../utils';
import { ProductCurrencyResyncEffect } from './ProductCurrencyResyncEffect';
import { QuoteDrawerSections, QuoteSection } from './QuoteDrawerSections';
import { QuoteEditLockNotice } from './QuoteEditLockNotice';

type QuoteDrawerMainPageProps = {
  formikQuote: QuoteFormikType;
  curProductsRef: MutableRefObject<Record<string, Product>>;

  mode: QuoteDrawerMode;
  submitType: QuoteSubmitType;
  submitForm: (values: QuoteDrawerFormikType) => Promise<void> | void;
  cancelForm: () => Promise<void> | void;
  isSubmitPending: boolean;

  /** @default 'Quote Information' */
  initialSection: QuoteSection | undefined;
  /** @default false */
  isPlantArchived?: boolean;
  /** @default false */
  isQuoteArchived?: boolean;
  loadedQuoteID?: string | null;
  onArchiveSuccess?: (result: QuoteArchiveResult) => void;
  filesManager: FileUploadsManager;
  openedFromProject?: boolean;
};

export const QuoteDrawerMainPage = ({
  formikQuote,
  initialSection = QuoteSection.QUOTE_INFORMATION,
  curProductsRef,
  mode,
  submitType,
  submitForm,
  cancelForm,
  isSubmitPending,
  isPlantArchived = false,
  isQuoteArchived = false,
  loadedQuoteID = null,
  onArchiveSuccess,
  filesManager,
  openedFromProject = false,
}: QuoteDrawerMainPageProps): ReactElement | null => {
  const ctx = useContext(SlabContext);
  const { guardClose, editTracker } = useDrawerCloseGuard();

  const { readOnly, lockedBy, isReconcilingBuyerPricing } = useQuoteDrawerContext();
  const { confirmAndArchive, isLoading: isArchiveLoading } = useArchiveManager({
    entity: 'POST archive quote by ID',
    onSuccess: onArchiveSuccess,
  });

  const requiredFields: RequiredQuoteFieldsInfo =
    mode === QuoteDrawerMode.Quick
      ? {
          company: true,
          contact: false,
          terms: false,
          configurationTemplate: false,
          expirationDate: false,
          project: false,
          productDeliveryType: false,
          projectJobNumber: false,
          lossDetails: false,
          priceEscalation: false,
        }
      : {
          company: ctx.userInfo.hasFlags([Enums.FeatureFlagName.FeatureFlagRequiresQuoteCompany]),
          contact: ctx.userInfo.hasFlags([
            Enums.FeatureFlagName.FeatureFlagRequiresQuoteCompanyContact,
          ]),
          terms: ctx.userInfo.hasFlags([Enums.FeatureFlagName.FeatureFlagRequiresQuoteTerms]),
          configurationTemplate: ctx.userInfo.hasFlags([
            Enums.FeatureFlagName.FeatureFlagRequiresQuoteQuoteConfig,
          ]),
          expirationDate: ctx.userInfo.hasFlags([
            Enums.FeatureFlagName.FeatureFlagRequiresQuoteExpirationDate,
          ]),
          project: true,
          productDeliveryType: ctx.userInfo.hasFlags([
            Enums.FeatureFlagName.FeatureFlagRequiresProductDeliveryType,
          ]),
          projectJobNumber: ctx.userInfo.hasFlags([
            Enums.FeatureFlagName.FeatureFlagRequiresQuoteProjectJobNumber,
          ]),
          lossDetails: ctx.userInfo.hasFlags([
            Enums.FeatureFlagName.FeatureFlagRequiresQuoteLossDetails,
          ]),
          priceEscalation: ctx.userInfo.hasFlags([
            Enums.FeatureFlagName.FeatureFlagRequiresQuotePriceEscalation,
          ]),
        };

  const isMultiLocationEnabled = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagMultiLocationEnabled,
  ]);
  const requiresContactInfo = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagRequiresContactInfo,
  ]);
  const requiresContactEmail = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagRequiresContactEmail,
  ]);
  const [originalProductsRef] = useState(structuredClone(curProductsRef));

  const hasSeededFromProjectRef = useRef(false);
  const pendingProjectChangeRef = useRef(false);

  const isNew = formikQuote.id === NIL_UUID;

  const { marginConfiguration, defaultMargins } = useTenantMargins();
  const hasTierPricing =
    ctx.userInfo.hasFlags([Enums.FeatureFlagName.FeatureFlagTierPricing]) &&
    ctx.userInfo.tenant.tierPricing;
  const { data: fixedPricingByCompanyForReset, isLoading: isLoadingFixedPricingForReset } =
    useSlabQuery(
      'GET tier pricing by company ID',
      { queryParams: { companyIds: [formikQuote.company?.id ?? ''] } },
      { enabled: hasTierPricing && hasStringValue(formikQuote.company?.id) },
    );

  const {
    isLoading: isLoadingQuoteStatuses,
    isError: isErrorQuoteStatuses,
    data: quoteStatusList,
  } = useSlabQuery('GET quote statuses', {});

  const {
    isLoading: isLoadingQuotePolicies,
    isError: isErrorQuotePolicies,
    data: quotePolicies,
  } = useSlabQuery('GET quote policies', {});

  const isLoading = isLoadingQuoteStatuses || isLoadingQuotePolicies || quotePolicies === undefined;
  const isError = isErrorQuoteStatuses || isErrorQuotePolicies;

  if (isLoading) {
    return <LoadingSpinner />;
  }
  if (isError || quoteStatusList === undefined) {
    return <div>ERROR</div>;
  }

  const currentRevisionIsEditable = canEditCurrentRevision({
    requiresQuoteApprovals: getTenantRequiresApprovals({
      tenant: ctx.userInfo.tenant,
      quotePolicies,
    }),
    savedQuoteStatus: isValidUUID(formikQuote.status.id)
      ? quoteStatusList.items.find((s) => s.id === formikQuote.status.id)
      : QuoteStatus.zero(),
  });

  const isViewOnly = hasValue(lockedBy) || isQuoteArchived;

  const drawerTitle = ((): string => {
    if (mode === QuoteDrawerMode.Quick) return 'Create a quote';
    if (submitType === 'saveAsNewRevision') {
      return 'Create a quote revision';
    }
    if (isNew || submitType === 'saveAsNewQuote') {
      return 'Create a quote';
    }
    return 'Edit a quote';
  })();

  // QuoteDrawerFormikType is used for the Formik generic even in Full mode. In Full
  // mode the quick-only fields (`quickProjectMode`, `quickAddress`) are ignored by
  // QuoteDrawer.upsertQuote so they are safe to carry through.
  const quickModeInitialValues: QuoteDrawerFormikType = {
    ...formikQuote,
    quickProjectMode: 'new' as QuickProjectMode,
    quickAddress: emptyQuickQuoteAddress(),
  };

  const getResetValues = (values: QuoteDrawerFormikType): QuoteDrawerFormikType => {
    const displayOnlyContext = {
      curProductsRef: originalProductsRef,
      marginConfiguration,
      defaultMargins,
    };

    return {
      ...values,
      mainProductsByPlants: applyTierPricingToProductsByPlants(
        values.mainProductsByPlants,
        fixedPricingByCompanyForReset,
        displayOnlyContext,
      ),
      additionalProductsByPlants: applyTierPricingToProductsByPlants(
        values.additionalProductsByPlants,
        fixedPricingByCompanyForReset,
        displayOnlyContext,
      ),
    };
  };

  return (
    <Formik<QuoteDrawerFormikType>
      validationSchema={NewQuoteSchemaFormik(
        requiredFields,
        requiresContactInfo,
        isMultiLocationEnabled,
        requiresContactEmail,
      )}
      initialValues={quickModeInitialValues}
      validate={(values): Record<string, unknown> => {
        if (mode !== QuoteDrawerMode.Quick) {
          return {};
        }
        if (values.quickProjectMode === 'existing' && !hasValue(values.project?.id)) {
          return { project: { id: 'Project is required when adding to an existing project' } };
        }
        if (values.quickProjectMode === 'new') {
          if (!hasStringValue(values.quickAddress.state)) {
            return { quickAddress: { state: 'State is required' } };
          }
        }
        return {};
      }}
      validateOnMount={!isNew}
      initialTouched={{
        mainProductsByPlants: formikQuote.mainProductsByPlants.map((plantGroup) => ({
          products: plantGroup.products.map(() => ({
            product: {
              option: true,
              incompatibleMixQuantityUnits: true,
              allowIncompatibleMixQuantityUnits: true,
            },
          })),
        })),
        additionalProductsByPlants: formikQuote.additionalProductsByPlants.map((plantGroup) => ({
          products: plantGroup.products.map(() => ({
            product: {
              option: true,
              incompatibleMixQuantityUnits: true,
              allowIncompatibleMixQuantityUnits: true,
            },
          })),
        })),
      }}
      onSubmit={(values): Promise<void> | void => submitForm(values)}
    >
      {(formikBag): ReactElement => {
        const requestClose = (): void =>
          guardClose({
            isDirty:
              !readOnly &&
              !isViewOnly &&
              (editTracker.isEdited() ||
                filesManager.hasAttachableItems ||
                filesManager.hasPendingUploads ||
                filesManager.excludedCloneFileIds.length > 0),
            canSave: formikBag.isValid && !isReconcilingBuyerPricing,
            // Submit awaits the dispatch-customer check before any request starts.
            isSaving: isSubmitPending || formikBag.isSubmitting,
            onSave: formikBag.submitForm,
            onDiscard: cancelForm,
          });

        return (
          <SlabDrawerLayout>
            <DrawerCloseHandler onClose={requestClose} editTracker={editTracker} />
            <Box>
              <FormErrorNotification />
              <ProductCurrencyResyncEffect />
              {!readOnly && (
                <SlabDrawerHeader
                  title={
                    hasValue(lockedBy) ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant='h1' fontSize='2rem'>
                          {drawerTitle}
                        </Typography>
                        <Tag variant='Blue' label='View only' icon={Eye} />
                      </Box>
                    ) : (
                      drawerTitle
                    )
                  }
                  onClose={requestClose}
                />
              )}
              {hasValue(lockedBy) && (
                <Box sx={{ mb: 2 }}>
                  <QuoteEditLockNotice
                    message={lockedBy.message}
                    editorName={lockedBy.editorName}
                  />
                </Box>
              )}
              {!readOnly && mode === QuoteDrawerMode.Quick && (
                <Box sx={{ mt: 2 }}>
                  <InfoBanner>
                    Fill in the basics. You can add details on the quote page later.
                  </InfoBanner>
                </Box>
              )}
              {!isNew && isPlantArchived && <ArchivedPlantWarning entityName='quote' />}

              <QuoteDrawerSections
                initialSection={initialSection}
                curProductsRef={curProductsRef}
                requiredFields={requiredFields}
                submitType={submitType}
                closeDrawer={cancelForm}
                mode={mode}
                filesManager={filesManager}
                isViewOnly={isViewOnly}
                openedFromProject={openedFromProject}
                hasSeededFromProjectRef={hasSeededFromProjectRef}
                pendingProjectChangeRef={pendingProjectChangeRef}
              />
            </Box>
            <Box>
              {mode === QuoteDrawerMode.Quick && (
                <Grid container spacing={2} sx={{ '& button': { width: '100%' } }}>
                  <Grid item xs={4}>
                    <SlabButton
                      variant='slab-secondary'
                      disabled={isSubmitPending}
                      onClick={requestClose}
                    >
                      Cancel
                    </SlabButton>
                  </Grid>
                  <Grid item xs={4}>
                    <SlabButton
                      variant='slab-secondary'
                      onClick={(): void => {
                        formikBag.resetForm({ values: getResetValues(quickModeInitialValues) });
                        curProductsRef.current = originalProductsRef.current;
                        pendingProjectChangeRef.current = false;
                      }}
                      disabled={
                        !formikBag.dirty ||
                        isSubmitPending ||
                        isLoadingFixedPricingForReset ||
                        isReconcilingBuyerPricing
                      }
                    >
                      Reset
                    </SlabButton>
                  </Grid>
                  <Grid item xs={4}>
                    <SlabButton
                      variant='slab-primary'
                      disabled={isSubmitPending || isReconcilingBuyerPricing}
                      loading={isSubmitPending}
                      onClick={(): void => {
                        formikBag.submitForm();
                      }}
                    >
                      Create Quote
                    </SlabButton>
                  </Grid>
                </Grid>
              )}
              {mode !== QuoteDrawerMode.Quick && !readOnly && !hasValue(lockedBy) && (
                <DrawerFormFooter
                  isArchived={isQuoteArchived}
                  isNew={isNew}
                  hasEntity={loadedQuoteID !== null && loadedQuoteID !== undefined}
                  isArchiveLoading={isArchiveLoading}
                  isSaving={isSubmitPending}
                  isSaveDisabled={
                    (submitType === 'save' && !currentRevisionIsEditable) ||
                    isReconcilingBuyerPricing ||
                    !(
                      formikBag.dirty ||
                      formikBag.touched.mainProductsByPlants !== undefined ||
                      formikBag.touched.additionalProductsByPlants !== undefined ||
                      isSubmitPending
                    )
                  }
                  isResetDisabled={
                    !formikBag.dirty ||
                    isSubmitPending ||
                    isArchiveLoading ||
                    isLoadingFixedPricingForReset ||
                    (isReconcilingBuyerPricing ?? false)
                  }
                  onArchive={(): void => {
                    if (loadedQuoteID)
                      confirmAndArchive({ id: loadedQuoteID, isArchived: isQuoteArchived });
                  }}
                  onCancel={requestClose}
                  onReset={(): void => {
                    formikBag.resetForm({ values: getResetValues(quickModeInitialValues) });
                    curProductsRef.current = originalProductsRef.current;
                    pendingProjectChangeRef.current = false;
                  }}
                  onSave={formikBag.submitForm}
                />
              )}
              {mode !== QuoteDrawerMode.Quick && (readOnly || hasValue(lockedBy)) && (
                <Grid container justifyContent='flex-end' sx={{ '& button': { width: '100%' } }}>
                  <Grid item xs={4}>
                    <SlabButton
                      variant='slab-secondary'
                      onClick={requestClose}
                      disabled={isSubmitPending}
                    >
                      Close
                    </SlabButton>
                  </Grid>
                </Grid>
              )}

              {!readOnly && !isViewOnly && (
                <Typography marginTop='12px'>
                  To save, address any highlighted issues in red first.
                </Typography>
              )}
            </Box>
          </SlabDrawerLayout>
        );
      }}
    </Formik>
  );
};
